import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTimeout(() => el.classList.add("in"), delay);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return (
    <Tag ref={ref} className={`reveal ${className}`}>
      {children}
    </Tag>
  );
}

export function Tag({ children, light = false, className = "" }) {
  return (
    <span
      className={`label-md inline-flex items-center gap-2 ${className}`}
      style={{ color: light ? "rgba(249,249,248,0.55)" : "var(--color-tertiary)" }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "var(--color-tertiary)",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {children}
    </span>
  );
}

export function SectionNum({ children, light = false }) {
  return (
    <span className="label-sm tabular" style={{ color: light ? "rgba(249,249,248,0.5)" : "var(--color-tertiary)" }}>
      {children}
    </span>
  );
}

export function DotDivider({ className = "" }) {
  return <div className={`dot-divider ${className}`} />;
}

export function RedDot({ size = "lg", className = "", style = {} }) {
  return <span className={`${size === "sm" ? "dot-sm" : "dot"} ${className}`} style={style} />;
}

export function CredentialPill({ children, light = false }) {
  return (
    <span
      className="label-sm"
      style={{
        color: light ? "rgba(249,249,248,0.55)" : "var(--color-on-surface-variant)",
        border: light ? "1px solid rgba(249,249,248,0.18)" : "1px solid var(--color-outline)",
        padding: "6px 12px",
        borderRadius: "var(--radius-sm)",
        display: "inline-block",
      }}
    >
      {children}
    </span>
  );
}

export function ArrowLink({ to, href, external, children, light = false, ...rest }) {
  const sharedClass =
    "group inline-flex items-center gap-2 label-md cursor-pointer";
  const sharedStyle = {
    color: light ? "var(--color-on-primary)" : "var(--color-primary)",
    paddingBottom: "4px",
    borderBottom: light
      ? "1.5px solid rgba(249,249,248,0.4)"
      : "1.5px solid var(--color-on-surface)",
    transition: "border-color 0.18s ease, color 0.18s ease, opacity 0.18s ease",
  };
  const hover = (e) => (e.currentTarget.style.opacity = "0.65");
  const leave = (e) => (e.currentTarget.style.opacity = "1");
  if (href) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        className={sharedClass}
        style={sharedStyle}
        onMouseEnter={hover}
        onMouseLeave={leave}
        {...rest}
      >
        {children}
        <span className="transition-transform group-hover:translate-x-1">→</span>
      </a>
    );
  }
  return (
    <Link
      to={to}
      className={sharedClass}
      style={sharedStyle}
      onMouseEnter={hover}
      onMouseLeave={leave}
      {...rest}
    >
      {children}
      <span className="transition-transform group-hover:translate-x-1">→</span>
    </Link>
  );
}

export function PrimaryButton({ children, className = "", as = "button", to, ...rest }) {
  if (as === "link" && to) {
    return (
      <Link to={to} className={`btn btn-primary ${className}`} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <button className={`btn btn-primary ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function GhostLightButton({ children, className = "", to, ...rest }) {
  if (to) {
    return (
      <Link to={to} className={`btn btn-ghost-light ${className}`} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <button className={`btn btn-ghost-light ${className}`} {...rest}>
      {children}
    </button>
  );
}
